import SplitButton from '@/Components/inputs/SplitButton';
import { usePageStore } from '@/Components/templates/PageStore';
import { emptyTrashMutation } from '@/graphql/mutations/BatchPostMutation.mutation';
import { PostStatusEnum } from '@/graphql/types';
import { Box, Button, Modal } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useMultiViewPickerStore } from '../MultiViewPickerStore';
import type { ActionBarProps } from './ActionBar';
import SelectActionReviewUsage, { currentPostTypeFromRoute } from './SelectActionReviewUsage';

type Props = { view: ActionBarProps['view'] };

const SelectActionsConst = [
  [PostStatusEnum.Trash, 'Move to Trash'],
  [PostStatusEnum.Publish, 'Set to Publish'],
  [PostStatusEnum.Hidden, 'Set to Private'],
  [PostStatusEnum.Draft, 'Set to Draft'],
] as Array<[PostStatusEnum, string]>;

export type { SelectActionsConst };

const SelectActions = ({ view }: Props) => {
  const [reviewUsageModal, setReviewUsageModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<(typeof SelectActionsConst)[number] | null>(null);
  const [actions, setActions] = useState<typeof SelectActionsConst>(SelectActionsConst);

  const isSelectAll = useMultiViewPickerStore((state) => state.isSelectAll);
  const selectAll = useMultiViewPickerStore((state) => state.selectAll);
  const deselectAll = useMultiViewPickerStore((state) => state.deselectAll);
  const count = useMultiViewPickerStore((state) => state.selectedNodesCount);
  const total = useMultiViewPickerStore((state) => state.nodes.size);

  const isPageLoading = usePageStore((state) => state.pageLoading);

  const [isTrash, setIsTrash] = useState(false);

  const filters = useSearch({
    strict: false,
    select: (state) => {
      return {
        visibility: state.visibility,
      };
    },
  });

  // Set up an effect to monitor changes to the visibility filter
  useEffect(() => {
    const statusIn = filters?.visibility;

    if (!statusIn || statusIn.length !== 1) {
      // we only want to filter actions when there is only one visibility
      setActions(SelectActionsConst);
      setIsTrash(false);
      return;
    }

    if (statusIn[0] === PostStatusEnum.Trash) {
      setIsTrash(true);
    }

    const visibility = statusIn[0];
    setActions(SelectActionsConst.filter((action) => action[0] !== visibility));
  }, [filters?.visibility]);

  const openConfirmationModal = (idx: number) => {
    setSelectedAction(actions[idx]);
    setReviewUsageModal(true);
  };

  const closeConfirmationModal = () => {
    setReviewUsageModal(false);
    setSelectedAction(null);
  };

  return (
    <>
      {/* when in grid -> we need to show a select all button here */}
      {view === 'grid' ? (
        <Button className="z-btn-1" onClick={isSelectAll ? deselectAll : selectAll}>
          {isSelectAll ? 'Deselect All' : 'Select All'}
        </Button>
      ) : null}
      {isTrash ? (
        <Button
          className="z-btn-1"
          disabled={isPageLoading}
          onClick={() => {
            const isConfirmed = window.confirm(
              'Are you sure you want to empty the trash? This action cannot be undone, everything in the trash will be permanently deleted.'
            );
            if (!isConfirmed) {
              return;
            }

            const postType = currentPostTypeFromRoute();
            if (!postType) {
              toast.error('Error emptying trash: postType not found');
              return;
            }
            usePageStore.setState({ pageLoading: true });
            emptyTrashMutation(
              {
                input: {
                  postType,
                },
              },
              {
                onCompleted: (data, errors) => {
                  usePageStore.setState({ pageLoading: false });
                  if (!!errors?.length) {
                    toast.error(`Error emptying trash: ${errors?.map((e) => e.message).join(', ')}`);
                    return;
                  }
                  toast.success('Trash emptied');
                },
              }
            );
          }}
        >
          Empty Trash
        </Button>
      ) : null}
      <div
        style={{ display: 'flex', alignItems: 'center', height: '100%' }}
        className={count > 0 ? 'z-show' : 'z-hide'}
      >
        <SplitButton
          className="SplitBtn"
          style={{ marginRight: '1rem', height: '100%' }} // Remove the height property
          options={actions.map((action) => action[1])}
          mainButton={(option, idx) => {
            return (
              <Button
                disabled={isPageLoading}
                onClick={() => {
                  openConfirmationModal(idx);
                }}
              >
                {option}
              </Button>
            );
          }}
        />
        <div>
          {count} of {total} selected
        </div>
      </div>
      {/* Confirmation Modal */}
      <Modal
        open={reviewUsageModal}
        onClose={closeConfirmationModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ maxWidth: '95%' }}>
          {reviewUsageModal && selectedAction ? (
            <SelectActionReviewUsage selectedAction={selectedAction} closeModal={closeConfirmationModal} />
          ) : (
            <></>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default SelectActions;
